/* Entrepreneurship hands-on founder simulator — pure data + logic.
   No backend. State lives in the page; commit only happens on quiz pass via
   useModuleRewards (which already no-ops for admin/QA users). */

export type BusinessTypeKey =
  | "service" | "product" | "retail" | "creator" | "saas" | "marketplace";

export type Audience = "general" | "young_adults" | "families" | "professionals";
export type Pricing  = "budget" | "balanced" | "premium";
export type Strategy = "lean" | "balanced" | "aggressive";

export interface BusinessType {
  key: BusinessTypeKey;
  name: string;
  examples: string;
  howItMakesMoney: string;
  primaryRisk: string;
  metricsShown: string[];
  hiddenTradeoff: string;
  difficulty: "Low" | "Medium" | "High";
  riskLabel: "Low" | "Medium" | "High";
  growthLabel: "Low" | "Medium" | "High";
  startupCost: number;          // suggested startup capital
  fixedMonthlyCost: number;
  variableCostPerCustomer: number;
  basePriceByPricing: Record<Pricing, number>;
  baseConversion: number;       // 0..1
  baseLeads: number;            // baseline monthly inbound interest
}

export const BUSINESS_TYPES: Record<BusinessTypeKey, BusinessType> = {
  service: {
    key: "service", name: "Service Business",
    examples: "Lawn care, cleaning, tutoring, consulting, pet care",
    howItMakesMoney: "Time × price × repeat customers × reputation",
    primaryRisk: "Owner capacity and burnout",
    metricsShown: ["Bookings", "Repeat customers", "Satisfaction"],
    hiddenTradeoff: "Capacity ceiling — you can only do so much yourself.",
    difficulty: "Low", riskLabel: "Low", growthLabel: "Medium",
    startupCost: 3000, fixedMonthlyCost: 600, variableCostPerCustomer: 8,
    basePriceByPricing: { budget: 40, balanced: 70, premium: 110 },
    baseConversion: 0.35, baseLeads: 18,
  },
  product: {
    key: "product", name: "Product / E-commerce",
    examples: "Apparel, handmade goods, supplements, accessories",
    howItMakesMoney: "Units sold × margin, less inventory + ads + shipping",
    primaryRisk: "Cash trapped in inventory that doesn't sell",
    metricsShown: ["Units sold", "Gross margin", "Inventory", "Returns"],
    hiddenTradeoff: "Inventory is cash. Wrong product = trapped capital.",
    difficulty: "Medium", riskLabel: "Medium", growthLabel: "High",
    startupCost: 8000, fixedMonthlyCost: 900, variableCostPerCustomer: 14,
    basePriceByPricing: { budget: 28, balanced: 48, premium: 85 },
    baseConversion: 0.22, baseLeads: 60,
  },
  retail: {
    key: "retail", name: "Local Retail / Food",
    examples: "Food stand, pop-up shop, drink stand, market booth",
    howItMakesMoney: "Foot traffic × ticket size, minus rent + spoilage",
    primaryRisk: "Fixed costs on slow days, spoilage on quiet weeks",
    metricsShown: ["Foot traffic", "Avg ticket", "Spoilage", "Booth/rent"],
    hiddenTradeoff: "Bad location/weather can sink a perfectly run booth.",
    difficulty: "Medium", riskLabel: "High", growthLabel: "Medium",
    startupCost: 6000, fixedMonthlyCost: 1400, variableCostPerCustomer: 6,
    basePriceByPricing: { budget: 9, balanced: 15, premium: 22 },
    baseConversion: 0.55, baseLeads: 70,
  },
  creator: {
    key: "creator", name: "Creator / Media",
    examples: "Newsletter, YouTube, podcast, content brand",
    howItMakesMoney: "Trust → offers, sponsorships, products, email list",
    primaryRisk: "Vanity metrics that never convert to revenue",
    metricsShown: ["Followers", "Engagement", "Email list", "Sponsor trust"],
    hiddenTradeoff: "Followers ≠ money. Trust + offer ≠ optional.",
    difficulty: "Medium", riskLabel: "Medium", growthLabel: "High",
    startupCost: 2000, fixedMonthlyCost: 350, variableCostPerCustomer: 2,
    basePriceByPricing: { budget: 12, balanced: 24, premium: 60 },
    baseConversion: 0.06, baseLeads: 220,
  },
  saas: {
    key: "saas", name: "SaaS / App",
    examples: "Productivity app, scheduling tool, niche software",
    howItMakesMoney: "Paying users × subscription price minus churn",
    primaryRisk: "Building too much before validating real demand",
    metricsShown: ["Users", "Paying users", "Churn", "MRR"],
    hiddenTradeoff: "Free users aren't customers. Churn quietly kills MRR.",
    difficulty: "High", riskLabel: "High", growthLabel: "High",
    startupCost: 12000, fixedMonthlyCost: 1800, variableCostPerCustomer: 3,
    basePriceByPricing: { budget: 9, balanced: 19, premium: 39 },
    baseConversion: 0.05, baseLeads: 320,
  },
  marketplace: {
    key: "marketplace", name: "Marketplace / Platform",
    examples: "Local services marketplace, creator marketplace",
    howItMakesMoney: "Take rate on matched buyer-seller transactions",
    primaryRisk: "No liquidity on one side of the market",
    metricsShown: ["Buyers", "Sellers", "Matches", "Trust issues"],
    hiddenTradeoff: "Two-sided cold-start: one weak side breaks the whole thing.",
    difficulty: "High", riskLabel: "High", growthLabel: "High",
    startupCost: 14000, fixedMonthlyCost: 2200, variableCostPerCustomer: 4,
    basePriceByPricing: { budget: 8, balanced: 16, premium: 30 },
    baseConversion: 0.04, baseLeads: 380,
  },
};

/* ─── Audience / Pricing / Strategy modifiers ─── */

export const AUDIENCE_INFO: Record<Audience, { label: string; summary: string; convMul: number; brand: number; risk: number }> = {
  general:        { label: "General Public",     summary: "Bigger reach, more competition, lower conversion.",  convMul: 0.85, brand: 0,  risk: 0 },
  young_adults:   { label: "Young Adults",       summary: "Trend-sensitive, viral upside, lower spending power.", convMul: 1.0, brand: 5,  risk: 5 },
  families:       { label: "Families",           summary: "Higher trust bar; loyal once satisfied.",            convMul: 0.95, brand: 10, risk: 0 },
  professionals:  { label: "Professionals",      summary: "Higher spend, higher expectations, less patience.",  convMul: 0.9,  brand: 8,  risk: 5 },
};

export const PRICING_INFO: Record<Pricing, { label: string; summary: string; priceMul: number; convMul: number; brandMod: number; burnMod: number }> = {
  budget:   { label: "Budget",   summary: "More customers, thinner margin, higher workload.",         priceMul: 0.75, convMul: 1.25, brandMod: -5, burnMod: 10 },
  balanced: { label: "Balanced", summary: "Moderate growth, moderate margin, stable positioning.",    priceMul: 1.0,  convMul: 1.0,  brandMod: 0,  burnMod: 0 },
  premium:  { label: "Premium",  summary: "Fewer buyers, higher margin, brand expectation is real.",  priceMul: 1.4,  convMul: 0.7,  brandMod: 10, burnMod: 0 },
};

export const STRATEGY_INFO: Record<Strategy, { label: string; summary: string; cashMul: number; mktBoost: number; riskMod: number }> = {
  lean:        { label: "Lean Startup",      summary: "Lower upfront spend, slower growth, more learning, less cash risk.", cashMul: 1.15, mktBoost: 10, riskMod: -5 },
  balanced:    { label: "Balanced Launch",   summary: "Moderate spend across product, marketing, and ops.",                  cashMul: 1.0,  mktBoost: 25, riskMod: 0 },
  aggressive:  { label: "Aggressive Growth", summary: "High upfront spend, fast acquisition, fast cash burn.",               cashMul: 0.8,  mktBoost: 60, riskMod: 15 },
};

/* ─── Glossary ─── */

export const ENT_GLOSSARY: { term: string; def: string }[] = [
  { term: "Cash runway",        def: "How many months the business can operate before running out of cash." },
  { term: "Profit",             def: "Revenue minus all expenses for the period. Not the same as cash." },
  { term: "Gross margin",       def: "Money left after the direct cost of delivering each unit or service." },
  { term: "Customer acquisition cost (CAC)", def: "What it costs you, on average, to acquire one paying customer." },
  { term: "Conversion rate",    def: "Share of leads that become paying customers." },
  { term: "Churn",              def: "Customers stopping or leaving over time. High churn quietly kills growth." },
  { term: "Burn rate",          def: "How fast the business spends cash beyond what it earns." },
  { term: "Inventory risk",     def: "Cash tied up in products that haven't sold yet." },
  { term: "Vanity metric",      def: "A number that looks impressive (followers, sign-ups) but doesn't drive revenue." },
  { term: "Brand trust",        def: "Reputation capital — affects future leads, conversion, and pricing power." },
  { term: "Operational stability", def: "How reliably you deliver. Weak ops = bad reviews, returns, and churn." },
  { term: "Founder burnout",    def: "Owner exhaustion. Tanks consistency and quality if ignored." },
];

/* ─── Decision panels ─── */

export type DecisionPanelKey =
  | "pricing" | "marketing" | "hiring" | "operations" | "reinvest" | "expand";

export interface Effects {
  cash?: number;             // one-off cash delta
  fixedMonthlyCost?: number; // ongoing delta
  avgPrice?: number;         // multiplier on avgPrice (e.g. 1.1)
  conversionMul?: number;
  marketingBoost?: number;   // adds to current marketingBoost
  brandTrust?: number;
  satisfaction?: number;
  opsStability?: number;
  burnout?: number;
  riskLevel?: number;
  employees?: number;
  debt?: number;
  debtMonthlyPayment?: number;
  customers?: number;        // direct customer add/loss
  note: string;              // shown in debrief
}

export interface DecisionOption {
  key: string;
  label: string;
  bullets: string[];
  effects: Effects;
}

export interface DecisionPanel {
  key: DecisionPanelKey;
  title: string;
  prompt: string;
  options: DecisionOption[];
}

export const DECISION_PANELS: Record<DecisionPanelKey, DecisionPanel> = {
  pricing: {
    key: "pricing", title: "Pricing", prompt: "How will you price this month?",
    options: [
      { key: "raise",  label: "Raise prices",            bullets: ["+10% price", "Some buyers leave", "Margin improves"],
        effects: { avgPrice: 1.10, conversionMul: 0.9, brandTrust: 2, note: "Raised prices — margin up, conversion down a bit." } },
      { key: "lower",  label: "Lower prices",            bullets: ["-10% price", "More buyers", "Burnout rises"],
        effects: { avgPrice: 0.90, conversionMul: 1.15, burnout: 6, brandTrust: -3, note: "Lowered prices — more demand, more workload." } },
      { key: "keep",   label: "Keep balanced pricing",   bullets: ["No price change", "Stable positioning"],
        effects: { brandTrust: 1, note: "Held pricing steady to protect positioning." } },
      { key: "tier",   label: "Create tiered packages",  bullets: ["+5% effective price", "Better segmentation", "Ops slightly more complex"],
        effects: { avgPrice: 1.05, conversionMul: 1.05, opsStability: -3, note: "Launched tiered packages — better fit, more ops complexity." } },
    ],
  },
  marketing: {
    key: "marketing", title: "Marketing", prompt: "Where will you spend acquisition effort?",
    options: [
      { key: "organic",  label: "Organic content / referrals", bullets: ["No cash cost", "Slow but trusted leads", "+brand"],
        effects: { marketingBoost: 15, brandTrust: 5, note: "Leaned on organic + referrals — slow, trusted growth." } },
      { key: "paid",     label: "Paid ads",                    bullets: ["-$1,200 cash", "Big lead spike", "Risk if conversion is weak"],
        effects: { cash: -1200, marketingBoost: 55, riskLevel: 6, note: "Pushed paid ads — leads jumped, cash dropped." } },
      { key: "local",    label: "Local outreach / partnerships", bullets: ["-$300 cash", "Mid lead boost", "+brand"],
        effects: { cash: -300, marketingBoost: 25, brandTrust: 4, note: "Did local outreach — modest cost, mid boost." } },
      { key: "influencer", label: "Influencer / creator campaign", bullets: ["-$2,000 cash", "Spiky leads", "Risk if fit is wrong"],
        effects: { cash: -2000, marketingBoost: 70, riskLevel: 10, brandTrust: 2, note: "Ran an influencer campaign — spike or flop." } },
      { key: "none",     label: "No marketing this month",     bullets: ["No spend", "Pipeline shrinks", "Cash preserved"],
        effects: { marketingBoost: -10, note: "Skipped marketing to preserve cash — pipeline cooled." } },
    ],
  },
  hiring: {
    key: "hiring", title: "Hiring", prompt: "Your time is finite. What about the team?",
    options: [
      { key: "solo",      label: "Stay solo",                bullets: ["No payroll", "Capacity capped", "Burnout up if busy"],
        effects: { burnout: 4, note: "Stayed solo — kept cash, kept the ceiling." } },
      { key: "parttime",  label: "Hire part-time help",       bullets: ["+$900 fixed/mo", "-burnout", "+capacity"],
        effects: { fixedMonthlyCost: 900, employees: 1, burnout: -10, opsStability: 4, note: "Hired part-time help — capacity up, payroll begins." } },
      { key: "specialist",label: "Hire a specialist / contractor", bullets: ["-$1,800 one-off", "+ops quality"],
        effects: { cash: -1800, opsStability: 10, brandTrust: 4, note: "Brought in a specialist — quality and ops up." } },
      { key: "aggressive",label: "Hire aggressively (too early)", bullets: ["+$2,500 fixed/mo", "+capacity", "Cash burn spikes"],
        effects: { fixedMonthlyCost: 2500, employees: 3, burnout: -15, riskLevel: 18, opsStability: -5, note: "Over-hired — capacity up, runway down." } },
    ],
  },
  operations: {
    key: "operations", title: "Operations", prompt: "Operations is how promises get kept.",
    options: [
      { key: "systems", label: "Improve systems / SOPs",     bullets: ["-$400", "+ops", "+satisfaction"],
        effects: { cash: -400, opsStability: 10, satisfaction: 5, note: "Built systems — fewer mistakes, happier customers." } },
      { key: "tools",   label: "Buy tools / software",        bullets: ["-$700", "+ops", "+capacity"],
        effects: { cash: -700, opsStability: 8, burnout: -4, note: "Bought tools — small payback, less manual work." } },
      { key: "quality", label: "Tighten quality control",     bullets: ["-$500", "+satisfaction", "+brand"],
        effects: { cash: -500, satisfaction: 8, brandTrust: 5, note: "Tightened QC — quality up, returns down." } },
      { key: "ignore",  label: "Ignore ops, push sales",      bullets: ["No spend", "-ops", "-satisfaction over time"],
        effects: { opsStability: -8, satisfaction: -6, riskLevel: 6, note: "Ignored ops to chase sales — debt is accruing somewhere." } },
    ],
  },
  reinvest: {
    key: "reinvest", title: "Reinvest", prompt: "You earned some profit. Now what?",
    options: [
      { key: "save",      label: "Save the cash (reserve)",        bullets: ["+runway", "Slower growth"],
        effects: { riskLevel: -8, note: "Saved profit — runway up, growth steady." } },
      { key: "marketing", label: "Reinvest in marketing",          bullets: ["-$1,500", "Big lead boost"],
        effects: { cash: -1500, marketingBoost: 60, riskLevel: 4, note: "Reinvested in marketing — leads jumped." } },
      { key: "product",   label: "Reinvest in product / equipment", bullets: ["-$2,000", "+ops", "+satisfaction"],
        effects: { cash: -2000, opsStability: 8, satisfaction: 6, brandTrust: 4, note: "Reinvested in product — quality up." } },
      { key: "cx",        label: "Reinvest in customer experience", bullets: ["-$1,000", "+satisfaction", "+brand"],
        effects: { cash: -1000, satisfaction: 10, brandTrust: 8, note: "Reinvested in CX — repeat customers + referrals climb." } },
    ],
  },
  expand: {
    key: "expand", title: "Expand", prompt: "Stable enough to expand?",
    options: [
      { key: "offer",   label: "Add a new service / product",    bullets: ["-$1,500", "+leads", "+ops complexity"],
        effects: { cash: -1500, marketingBoost: 25, opsStability: -6, riskLevel: 8, note: "Launched a new offer — bigger upside, more ops." } },
      { key: "market",  label: "Enter a new market / city",       bullets: ["-$3,000", "+leads", "+risk"],
        effects: { cash: -3000, marketingBoost: 45, riskLevel: 14, note: "Entered a new market — leads up, risk up." } },
      { key: "channel", label: "Open a new channel (online/wholesale)", bullets: ["-$1,200", "+leads", "+brand"],
        effects: { cash: -1200, marketingBoost: 30, brandTrust: 5, note: "Opened a new channel — distribution widened." } },
      { key: "early",   label: "Expand too early",                bullets: ["-$4,000", "+risk", "-ops"],
        effects: { cash: -4000, riskLevel: 25, opsStability: -12, brandTrust: -5, note: "Expanded too early — strained the business." } },
    ],
  },
};

/* ─── Event decks by business type ─── */

export interface SimEvent {
  id: string;
  title: string;
  body: string;
  options: DecisionOption[];
}

const universal: SimEvent[] = [
  {
    id: "review_bad", title: "A bad review just landed",
    body: "Someone publicly complained. The whole pipeline sees it.",
    options: [
      { key: "apologize", label: "Apologize publicly and fix it",   bullets: ["-$300", "+satisfaction", "+brand"],
        effects: { cash: -300, satisfaction: 6, brandTrust: 6, note: "Handled the review well — trust recovered." } },
      { key: "ignore",    label: "Ignore it",                         bullets: ["-brand", "-future leads"],
        effects: { brandTrust: -10, marketingBoost: -10, note: "Ignored the review — brand bruised." } },
      { key: "argue",     label: "Argue with the customer online",   bullets: ["--brand", "+risk"],
        effects: { brandTrust: -18, riskLevel: 10, marketingBoost: -15, note: "Argued publicly — brand sank." } },
      { key: "improve",   label: "Quietly improve the process",      bullets: ["-$200", "+ops"],
        effects: { cash: -200, opsStability: 8, note: "Used the review as input — ops improved." } },
    ],
  },
];

export const EVENT_DECKS: Record<BusinessTypeKey, SimEvent[]> = {
  service: [
    { id: "svc_overbooked", title: "You're overbooked", body: "12 jobs booked, you can handle 8 well.",
      options: [
        { key: "all",      label: "Accept all 12 jobs",          bullets: ["+revenue short term", "-satisfaction", "+burnout"],
          effects: { customers: 4, satisfaction: -10, burnout: 18, brandTrust: -5, note: "Accepted all 12 — quality dropped." } },
        { key: "waitlist", label: "Accept 8 and waitlist others", bullets: ["+brand", "Stable satisfaction"],
          effects: { customers: 2, brandTrust: 6, satisfaction: 4, note: "Held the line at 8 — brand stayed strong." } },
        { key: "hire",     label: "Hire part-time help fast",    bullets: ["-$1,200", "+capacity"],
          effects: { cash: -1200, employees: 1, fixedMonthlyCost: 600, customers: 3, note: "Hired help — capacity up, payroll begins." } },
        { key: "raise",    label: "Raise prices to reduce demand", bullets: ["+10% price", "-conversion"],
          effects: { avgPrice: 1.10, conversionMul: 0.85, brandTrust: 3, note: "Raised prices to manage demand." } },
      ] },
    ...universal,
    { id: "svc_gear", title: "Equipment broke",
      body: "Critical gear failed this week.",
      options: [
        { key: "fix",   label: "Repair it now",            bullets: ["-$800", "Stay on schedule"],
          effects: { cash: -800, satisfaction: 2, note: "Repaired — on schedule." } },
        { key: "rent",  label: "Rent a replacement",       bullets: ["-$400", "Slightly slower"],
          effects: { cash: -400, opsStability: -3, note: "Rented gear — got through it." } },
        { key: "skip",  label: "Skip those jobs this week", bullets: ["-customers", "-brand"],
          effects: { customers: -5, brandTrust: -8, note: "Skipped jobs — customers churned." } },
        { key: "buy",   label: "Upgrade with new gear",     bullets: ["-$2,200", "+ops long term"],
          effects: { cash: -2200, opsStability: 10, satisfaction: 4, note: "Upgraded — ops solid going forward." } },
      ] },
  ],
  product: [
    { id: "prod_inv", title: "First inventory batch decision", body: "Supplier offers a discount on big batches.",
      options: [
        { key: "huge",    label: "Order huge batch (discount)", bullets: ["-$7,000", "Cash trapped if it stalls"],
          effects: { cash: -7000, riskLevel: 15, marketingBoost: 5, note: "Bought a big batch — discounted but cash now tied up." } },
        { key: "small",   label: "Order a small test batch",    bullets: ["-$1,500", "Slower growth, less risk"],
          effects: { cash: -1500, riskLevel: -5, note: "Tested small — safer, slower." } },
        { key: "preorder",label: "Take preorders first",        bullets: ["+cash from preorders", "Fulfillment risk if late"],
          effects: { cash: 1800, riskLevel: 8, brandTrust: -2, note: "Preorders validated demand — pressure to ship." } },
        { key: "fast",    label: "Skip testing, ship fast",     bullets: ["-$3,000", "+returns risk"],
          effects: { cash: -3000, satisfaction: -8, riskLevel: 12, note: "Skipped testing — returns are coming." } },
      ] },
    { id: "prod_ship", title: "Shipping costs jumped",
      body: "Your carrier hiked rates 20%.",
      options: [
        { key: "absorb", label: "Absorb the cost",             bullets: ["-margin", "Satisfaction stable"],
          effects: { fixedMonthlyCost: 250, satisfaction: 2, note: "Absorbed cost — margin shrank." } },
        { key: "raise",  label: "Raise prices",                bullets: ["+5% price", "-conversion"],
          effects: { avgPrice: 1.05, conversionMul: 0.92, note: "Raised prices — protected margin." } },
        { key: "min",    label: "Free shipping over a minimum", bullets: ["+avg order value", "Some buyers drop"],
          effects: { avgPrice: 1.08, conversionMul: 0.95, note: "Added free-ship minimum — basket size up." } },
        { key: "cheap",  label: "Switch to cheap packaging",   bullets: ["+margin", "+damage risk"],
          effects: { fixedMonthlyCost: -100, satisfaction: -6, riskLevel: 6, note: "Cheap packaging — damage rate climbing." } },
      ] },
    ...universal,
  ],
  retail: [
    { id: "ret_booth", title: "Big weekend event opportunity",
      body: "Booth fee is $2,000. Foot traffic could be huge.",
      options: [
        { key: "pay",   label: "Pay the fee, prep inventory", bullets: ["-$2,000", "Big sales window", "Spoilage risk"],
          effects: { cash: -2000, customers: 25, brandTrust: 4, riskLevel: 5, note: "Worked the event — strong weekend." } },
        { key: "skip",  label: "Skip and conserve cash",      bullets: ["No spend", "Opportunity missed"],
          effects: { riskLevel: -3, note: "Skipped — kept cash, missed sales." } },
        { key: "split", label: "Split booth with another vendor", bullets: ["-$900", "Less visibility"],
          effects: { cash: -900, customers: 10, brandTrust: 1, note: "Split a booth — lower cost, lower upside." } },
        { key: "under", label: "Show up under-prepared",       bullets: ["-$2,000", "Lost sales"],
          effects: { cash: -2000, customers: 4, satisfaction: -6, note: "Under-prepared — left money on the table." } },
      ] },
    { id: "ret_spoil", title: "Unsold inventory may spoil",
      body: "Stuff is sitting. Decide fast.",
      options: [
        { key: "discount", label: "Discount it before it expires", bullets: ["+sales, -margin"],
          effects: { customers: 8, avgPrice: 0.95, note: "Cleared inventory — protected cash." } },
        { key: "wait",     label: "Hold prices, hope it sells",   bullets: ["Margin preserved", "Waste risk"],
          effects: { riskLevel: 10, satisfaction: -2, note: "Held prices — some product spoiled." } },
        { key: "bundle",   label: "Bundle with popular items",     bullets: ["+avg order", "Waste down"],
          effects: { avgPrice: 1.05, customers: 5, note: "Bundled — moved product cleanly." } },
        { key: "trash",    label: "Throw it away",                 bullets: ["-cash", "No learning"],
          effects: { cash: -500, note: "Trashed inventory — cash lost." } },
      ] },
    ...universal,
  ],
  creator: [
    { id: "cre_followers", title: "Followers spiked. Nobody bought.",
      body: "Big growth, zero monetization.",
      options: [
        { key: "viral",  label: "Keep chasing viral content", bullets: ["+followers", "+burnout", "Still no revenue"],
          effects: { brandTrust: 3, burnout: 12, note: "Kept chasing virality — exhausted, broke." } },
        { key: "offer",  label: "Build a clear offer",          bullets: ["+revenue potential", "Needs trust"],
          effects: { avgPrice: 1.15, conversionMul: 1.15, brandTrust: 4, note: "Built an offer — monetization started." } },
        { key: "email",  label: "Start an email list",          bullets: ["+future conversion", "+ownership"],
          effects: { brandTrust: 6, marketingBoost: 15, note: "Started a list — own your audience." } },
        { key: "fake",   label: "Buy followers",                bullets: ["+followers", "--trust", "--engagement"],
          effects: { brandTrust: -25, conversionMul: 0.7, riskLevel: 12, note: "Bought followers — trust collapsed." } },
      ] },
    { id: "cre_sponsor", title: "Bad-fit sponsor offered cash",
      body: "Money is nice. Product doesn't match your audience.",
      options: [
        { key: "take",    label: "Take the deal",            bullets: ["+$1,500", "-trust"],
          effects: { cash: 1500, brandTrust: -12, note: "Took the deal — audience noticed." } },
        { key: "decline", label: "Decline to protect trust",  bullets: ["No cash", "+trust"],
          effects: { brandTrust: 8, note: "Declined — trust intact." } },
        { key: "negotiate", label: "Negotiate creative control", bullets: ["+$900", "Stable trust"],
          effects: { cash: 900, brandTrust: 2, note: "Negotiated control — clean execution." } },
        { key: "hide",    label: "Promote it without disclosure", bullets: ["+$1,500", "--risk", "--trust"],
          effects: { cash: 1500, brandTrust: -20, riskLevel: 20, note: "Hidden promo — safety/trust hit." } },
      ] },
    ...universal,
  ],
  saas: [
    { id: "saas_conv", title: "Signups up, paid conversions flat",
      body: "People try the product. Nobody pays.",
      options: [
        { key: "features", label: "Build more features",         bullets: ["-$2,000", "+burnout", "May not fix it"],
          effects: { cash: -2000, burnout: 10, riskLevel: 8, note: "Built features — conversion didn't move." } },
        { key: "onboard",  label: "Interview users, fix onboarding", bullets: ["-$300", "+conversion"],
          effects: { cash: -300, conversionMul: 1.25, satisfaction: 6, note: "Fixed onboarding — paid conversion lifted." } },
        { key: "price",    label: "Slash the price",              bullets: ["+users", "-revenue per user"],
          effects: { avgPrice: 0.7, conversionMul: 1.3, brandTrust: -3, note: "Slashed price — more users, less revenue per." } },
        { key: "ads",      label: "Spend more on ads first",      bullets: ["-$2,500", "More leaks, not more sales"],
          effects: { cash: -2500, marketingBoost: 40, riskLevel: 12, note: "More ads on broken funnel — burned cash." } },
      ] },
    { id: "saas_support", title: "Support requests piling up",
      body: "Users are stuck and emailing constantly.",
      options: [
        { key: "ignore", label: "Ignore, ship features",        bullets: ["+churn", "-satisfaction"],
          effects: { satisfaction: -10, customers: -8, riskLevel: 10, note: "Ignored support — users churned." } },
        { key: "docs",   label: "Write help docs / improve UX", bullets: ["-$400", "+ops", "+satisfaction"],
          effects: { cash: -400, opsStability: 8, satisfaction: 6, note: "Docs + UX cleanup — fewer tickets." } },
        { key: "hire",   label: "Hire support too early",       bullets: ["+$1,500/mo fixed", "+satisfaction"],
          effects: { fixedMonthlyCost: 1500, employees: 1, satisfaction: 5, riskLevel: 6, note: "Hired support — runway down." } },
        { key: "pause",  label: "Pause new signups",            bullets: ["-leads", "+stability"],
          effects: { marketingBoost: -25, opsStability: 6, note: "Paused signups to stabilize." } },
      ] },
    ...universal,
  ],
  marketplace: [
    { id: "mkt_supply", title: "Buyers showing up, not enough sellers",
      body: "Demand without supply = empty searches.",
      options: [
        { key: "recruit", label: "Recruit sellers one by one", bullets: ["+supply", "Time-intensive"],
          effects: { burnout: 8, brandTrust: 6, customers: 6, note: "Manually recruited supply — slow but real." } },
        { key: "ads",     label: "Spend more on buyer ads",     bullets: ["-$2,500", "Mismatch worsens"],
          effects: { cash: -2500, marketingBoost: 40, satisfaction: -8, riskLevel: 12, note: "More buyer ads — worsened mismatch." } },
        { key: "incent",  label: "Pay seller incentives",       bullets: ["-$1,800", "+supply"],
          effects: { cash: -1800, customers: 10, brandTrust: 3, note: "Paid seller incentives — supply ticked up." } },
        { key: "wait",    label: "Wait and hope",               bullets: ["-trust", "-customers"],
          effects: { brandTrust: -10, customers: -6, note: "Did nothing — buyers left." } },
      ] },
    { id: "mkt_dispute", title: "A transaction blew up",
      body: "Buyer and seller both blame you.",
      options: [
        { key: "process", label: "Create a refund/dispute process", bullets: ["-$600 setup", "+trust both sides"],
          effects: { cash: -600, brandTrust: 8, opsStability: 6, note: "Built dispute process — trust held." } },
        { key: "buyer",   label: "Always side with buyers",       bullets: ["+buyer trust", "-seller trust"],
          effects: { brandTrust: -2, riskLevel: 6, note: "Auto-sided with buyers — sellers wary." } },
        { key: "seller",  label: "Always side with sellers",      bullets: ["+seller trust", "-buyer trust"],
          effects: { brandTrust: -4, satisfaction: -5, note: "Auto-sided with sellers — buyers wary." } },
        { key: "ignore",  label: "Ignore the dispute",            bullets: ["--trust", "+churn"],
          effects: { brandTrust: -18, customers: -8, riskLevel: 14, note: "Ignored it — both sides churned." } },
      ] },
    ...universal,
  ],
};

/* ─── Cash crunch ─── */

export const CRUNCH_OPTIONS: DecisionOption[] = [
  { key: "cut", label: "Cut expenses immediately",
    bullets: ["-fixed costs", "Growth slows", "Some satisfaction risk"],
    effects: { fixedMonthlyCost: -800, satisfaction: -5, marketingBoost: -20, note: "Slashed costs to stop the bleed." } },
  { key: "loan", label: "Take a small business loan",
    bullets: ["+$5,000 cash", "+$250/mo payment", "+risk"],
    effects: { cash: 5000, debt: 5000, debtMonthlyPayment: 250, riskLevel: 12, note: "Took a loan — bought time, took on debt." } },
  { key: "partner", label: "Bring in a partner / investor",
    bullets: ["+$10,000 cash", "Ownership cost", "Less control"],
    effects: { cash: 10000, brandTrust: 4, riskLevel: -5, note: "Brought in a partner — cash secured, ownership shared." } },
  { key: "shutdown", label: "Shut down / pause the business",
    bullets: ["Stop losing cash", "Module continues to debrief"],
    effects: { note: "Closed the business to stop further losses." } },
];

/* ─── Initial state ─── */

export type SimPhase =
  | "brief" | "type" | "create" | "budget"
  | "monthStart" | "decision" | "event" | "result" | "debrief"
  | "crunch" | "report" | "quiz" | "summary";

export interface SimState {
  phase: SimPhase;
  month: number;
  maxMonths: number;
  status: "active" | "failed" | "shutdown";

  businessType?: BusinessTypeKey;
  name: string;
  audience?: Audience;
  pricing?: Pricing;
  strategy?: Strategy;

  startingCash: number;
  cash: number;
  totalRevenue: number;
  totalExpenses: number;
  lastProfit: number;

  fixedMonthlyCost: number;
  variableCostPerCustomer: number;
  avgPrice: number;
  conversionRate: number;
  baseLeads: number;
  marketingBoost: number;

  customers: number;
  satisfaction: number;
  brandTrust: number;
  opsStability: number;
  burnout: number;
  riskLevel: number;
  employees: number;

  debt: number;
  debtMonthlyPayment: number;

  allocations: { product: number; tools: number; marketing: number; ops: number; legal: number; reserve: number };
  decisionThisMonth?: { panel: DecisionPanelKey; optionKey: string; note: string };
  eventThisMonth?: { eventId: string; optionKey: string; note: string };
  monthResult?: { leads: number; newCustomers: number; revenue: number; variable: number; fixed: number; profit: number };
  pendingEvent?: SimEvent;

  history: Array<{
    month: number;
    decision?: string; event?: string;
    revenue: number; profit: number; cash: number;
    customers: number; satisfaction: number; brandTrust: number;
    note: string;
  }>;

  bestDecision?: { label: string; profitImpact: number };
  worstDecision?: { label: string; profitImpact: number };
  crunchUsed: boolean;
}

const clamp = (v: number, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, v));

export function initialState(): SimState {
  return {
    phase: "brief", month: 0, maxMonths: 16, status: "active",
    name: "",
    startingCash: 0, cash: 0, totalRevenue: 0, totalExpenses: 0, lastProfit: 0,
    fixedMonthlyCost: 0, variableCostPerCustomer: 0, avgPrice: 0,
    conversionRate: 0, baseLeads: 0, marketingBoost: 0,
    customers: 0, satisfaction: 70, brandTrust: 50, opsStability: 60,
    burnout: 10, riskLevel: 20, employees: 0,
    debt: 0, debtMonthlyPayment: 0,
    allocations: { product: 35, tools: 10, marketing: 25, ops: 10, legal: 10, reserve: 10 },
    history: [], crunchUsed: false,
  };
}

export function configureBusiness(
  s: SimState,
  type: BusinessTypeKey,
  name: string,
  audience: Audience,
  pricing: Pricing,
  strategy: Strategy,
): SimState {
  const bt = BUSINESS_TYPES[type];
  const a = AUDIENCE_INFO[audience];
  const p = PRICING_INFO[pricing];
  const st = STRATEGY_INFO[strategy];
  const startingCash = Math.round(bt.startupCost * st.cashMul * 3); // give them runway
  return {
    ...s, businessType: type, name, audience, pricing, strategy,
    startingCash, cash: startingCash,
    fixedMonthlyCost: bt.fixedMonthlyCost,
    variableCostPerCustomer: bt.variableCostPerCustomer,
    avgPrice: Math.round(bt.basePriceByPricing[pricing] * 1),
    conversionRate: bt.baseConversion * a.convMul * p.convMul,
    baseLeads: bt.baseLeads,
    marketingBoost: st.mktBoost,
    brandTrust: clamp(50 + a.brand + p.brandMod),
    burnout: clamp(10 + p.burnMod),
    riskLevel: clamp(20 + a.risk + st.riskMod),
    phase: "budget",
  };
}

/** Apply budget allocations — under/overfunding shifts starting metrics. */
export function applyBudget(s: SimState): SimState {
  const a = s.allocations;
  // Each bucket is centered around its baseline. Deviations move metrics.
  const baseline = { product: 35, tools: 10, marketing: 25, ops: 10, legal: 10, reserve: 10 };
  const k = (cur: number, base: number) => (cur - base) / 100;
  const brand   = k(a.product,  baseline.product)   * 20;
  const ops     = k(a.ops,      baseline.ops)       * 30 + k(a.tools, baseline.tools) * 20;
  const mkt     = k(a.marketing,baseline.marketing) * 60;
  const risk    = -k(a.legal,   baseline.legal)     * 25 - k(a.reserve, baseline.reserve) * 20;
  const burn    = -k(a.tools,   baseline.tools)     * 15;
  // Deduct startup spend (everything except reserve) immediately.
  const spent = Math.round(s.startingCash * ((100 - a.reserve) / 100));
  return {
    ...s,
    cash: Math.round(s.startingCash * (a.reserve / 100)),
    brandTrust: clamp(s.brandTrust + brand),
    opsStability: clamp(s.opsStability + ops),
    marketingBoost: Math.max(0, s.marketingBoost + mkt),
    riskLevel: clamp(s.riskLevel + risk),
    burnout: clamp(s.burnout + burn),
    phase: "monthStart", month: 1,
    totalExpenses: s.totalExpenses + spent,
  };
}

/** Apply Effects to a state object — pure. */
export function applyEffects(s: SimState, e: Effects): SimState {
  const next: SimState = { ...s };
  if (e.cash) { next.cash += e.cash; if (e.cash < 0) next.totalExpenses += -e.cash; }
  if (e.fixedMonthlyCost) next.fixedMonthlyCost = Math.max(0, next.fixedMonthlyCost + e.fixedMonthlyCost);
  if (e.avgPrice) next.avgPrice = Math.max(1, Math.round(next.avgPrice * e.avgPrice));
  if (e.conversionMul) next.conversionRate = Math.max(0.005, next.conversionRate * e.conversionMul);
  if (e.marketingBoost) next.marketingBoost = Math.max(0, next.marketingBoost + e.marketingBoost);
  if (e.brandTrust) next.brandTrust = clamp(next.brandTrust + e.brandTrust);
  if (e.satisfaction) next.satisfaction = clamp(next.satisfaction + e.satisfaction);
  if (e.opsStability) next.opsStability = clamp(next.opsStability + e.opsStability);
  if (e.burnout) next.burnout = clamp(next.burnout + e.burnout);
  if (e.riskLevel) next.riskLevel = clamp(next.riskLevel + e.riskLevel);
  if (e.employees) next.employees = Math.max(0, next.employees + e.employees);
  if (e.debt) next.debt += e.debt;
  if (e.debtMonthlyPayment) next.debtMonthlyPayment += e.debtMonthlyPayment;
  if (e.customers) next.customers = Math.max(0, next.customers + e.customers);
  return next;
}

/** Run the month's results once decision + event are resolved. */
export function runMonth(s: SimState): SimState {
  const satFactor = 0.5 + (s.satisfaction / 100) * 0.8;     // 0.5..1.3
  const burnFactor = 1 - (s.burnout / 200);                  // 1..0.5
  const leads = Math.round(s.baseLeads + s.marketingBoost * 0.6 + s.brandTrust * 0.3 + s.employees * 4);
  const effectiveConv = Math.min(0.6, s.conversionRate * (s.opsStability / 65));
  const newCustomers = Math.max(0, Math.round(leads * effectiveConv * satFactor * burnFactor));
  const repeat = Math.round(s.customers * (s.satisfaction / 130));
  const active = newCustomers + repeat;
  const revenue = active * s.avgPrice;
  const variable = active * s.variableCostPerCustomer;
  const fixed = s.fixedMonthlyCost + s.debtMonthlyPayment;
  const profit = revenue - variable - fixed;
  const cash = s.cash + profit;
  const next: SimState = {
    ...s,
    customers: active,
    totalRevenue: s.totalRevenue + revenue,
    totalExpenses: s.totalExpenses + variable + fixed,
    lastProfit: profit,
    cash,
    marketingBoost: Math.max(0, s.marketingBoost * 0.55),
    burnout: clamp(s.burnout + (s.employees === 0 ? 3 : -2)),
    monthResult: { leads, newCustomers, revenue, variable, fixed, profit },
    phase: "debrief",
  };
  const noteParts: string[] = [];
  if (next.decisionThisMonth?.note) noteParts.push(next.decisionThisMonth.note);
  if (next.eventThisMonth?.note) noteParts.push(next.eventThisMonth.note);
  next.history = [
    ...s.history,
    {
      month: s.month,
      decision: s.decisionThisMonth?.optionKey,
      event: s.eventThisMonth?.optionKey,
      revenue, profit, cash,
      customers: active,
      satisfaction: s.satisfaction,
      brandTrust: s.brandTrust,
      note: noteParts.join(" · "),
    },
  ];
  // Track best/worst decision by profit impact
  if (s.decisionThisMonth) {
    const label = `${s.decisionThisMonth.panel}: ${s.decisionThisMonth.optionKey}`;
    if (!next.bestDecision || profit > next.bestDecision.profitImpact) {
      next.bestDecision = { label, profitImpact: profit };
    }
    if (!next.worstDecision || profit < next.worstDecision.profitImpact) {
      next.worstDecision = { label, profitImpact: profit };
    }
  }
  return next;
}

/** Derived metrics for the dashboard. */
export function derived(s: SimState) {
  const burnPerMonth = Math.max(1, s.fixedMonthlyCost + s.debtMonthlyPayment);
  const runway = s.cash <= 0 ? 0 : Math.max(0, s.cash / burnPerMonth);
  const health = clamp(
    50
    + (s.lastProfit > 0 ? 15 : -10)
    + (s.satisfaction - 50) * 0.2
    + (s.brandTrust - 50) * 0.2
    + (s.opsStability - 50) * 0.15
    - (s.burnout - 30) * 0.2
    - (s.riskLevel - 30) * 0.15
    + Math.min(runway, 12) * 1.5,
  );
  return { runway, health, burnPerMonth };
}

/** Pick a random event from the deck (or universal if missing). */
export function pickEvent(s: SimState, rng: () => number = Math.random): SimEvent {
  if (!s.businessType) return universal[0];
  const deck = EVENT_DECKS[s.businessType];
  return deck[Math.floor(rng() * deck.length)];
}

/** Final founder rating logic. */
export function founderRating(s: SimState): { title: string; tone: "good" | "warn" | "bad"; body: string } {
  if (s.status === "failed" || s.cash < 0) {
    return { title: "Business Failed: Out of Cash", tone: "bad",
      body: "You ran out of cash before the business found its footing. That is the most common founder failure mode." };
  }
  if (s.status === "shutdown") {
    return { title: "Business Paused / Closed", tone: "warn",
      body: "You chose to pause rather than burn more cash. A real founder skill: knowing when to stop." };
  }
  if (s.burnout >= 75) return { title: "Overextended Founder", tone: "warn",
    body: "You grew, but at the cost of your own capacity. Burnout silently breaks businesses." };
  if (s.brandTrust < 30) return { title: "Vanity Metric Founder", tone: "warn",
    body: "Numbers went up but trust didn't. Without trust, growth is hollow." };
  if (s.riskLevel >= 70 && s.lastProfit > 0) return { title: "Growth Hacker With Burn Risk", tone: "warn",
    body: "Profitable, but living dangerously. Strong upside, fragile downside." };
  if (s.cash > s.startingCash * 1.5 && s.satisfaction > 70 && s.brandTrust > 60)
    return { title: "Strategic Founder", tone: "good",
      body: "You grew cash and reputation together. That's the hardest balance to hit." };
  if (s.lastProfit > 0 && derived(s).runway > 6) return { title: "Cash Flow Operator", tone: "good",
    body: "You ran a profitable, stable business. Boring is beautiful in year one." };
  if (s.avgPrice > 0 && s.brandTrust > 70) return { title: "Premium Brand Builder", tone: "good",
    body: "You priced for value and earned the brand to back it up." };
  return { title: "Business Survived", tone: "warn",
    body: "You made it to the end without blowing up. That alone puts you ahead of most first-time founders." };
}